const LEVEL_PRIORITIES = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
};

export const LOG_LEVEL_STORAGE_KEY = 'cc.logger.level';
export const LOG_LEVELS = Object.freeze(Object.keys(LEVEL_PRIORITIES));
const REDACTED_VALUE = '[REDACTED]';
const MAX_SANITIZE_DEPTH = 4;

const SENSITIVE_KEY_PATTERN = /(token|authorization|cookie|secret|password|passcode|api[-_]?key|ssn|dob|birth|email|phone|address|patient|mrn|medicalrecord|firstname|lastname|fullname|name)$/i;
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_PATTERN = /bearer\s+[A-Za-z0-9._~-]+/gi;

const getWindowObject = () => {
    if (typeof window === 'undefined') return null;
    return window;
};

export const normalizeLogLevel = (value) => {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    return LOG_LEVELS.includes(normalized) ? normalized : null;
};

export const getDefaultLogLevel = (env) => {
    const environmentName = String(env?.ENVIRONMENT || '')
        .trim()
        .toLowerCase();

    if (environmentName === 'prod' || environmentName === 'production') {
        return 'warn';
    }

    return 'info';
};

const getConsoleForWindow = (targetWindow) => {
    if (targetWindow?.console) return targetWindow.console;
    if (typeof console !== 'undefined') return console;
    return null;
};

const evaluateArgSafely = (arg) => {
    if (typeof arg !== 'function') return arg;

    try {
        return arg();
    } catch {
        return '[logger metadata evaluation failed]';
    }
};

const redactString = (value) => {
    if (typeof value !== 'string') return value;

    return value
        .replace(BEARER_PATTERN, 'Bearer [REDACTED]')
        .replace(JWT_PATTERN, REDACTED_VALUE);
};

const shouldRedactKey = (key) => SENSITIVE_KEY_PATTERN.test(String(key || ''));

const sanitizeArg = (value, depth = 0) => {
    if (value == null) return value;
    if (depth > MAX_SANITIZE_DEPTH) return '[logger metadata depth limit reached]';

    if (typeof value === 'string') {
        return redactString(value);
    }

    if (Array.isArray(value)) {
        return value.map((item) => sanitizeArg(item, depth + 1));
    }

    if (typeof value === 'object') {
        return Object.entries(value).reduce((acc, [key, entryValue]) => {
            if (shouldRedactKey(key)) {
                acc[key] = REDACTED_VALUE;
                return acc;
            }

            acc[key] = sanitizeArg(entryValue, depth + 1);
            return acc;
        }, {});
    }

    return value;
};

export const createLogger = ({
    getWindow = getWindowObject,
    storageKey = LOG_LEVEL_STORAGE_KEY,
    prefix = '[CC]',
} = {}) => {
    const getStoredLevel = () => {
        const targetWindow = getWindow();
        try {
            const storage = targetWindow?.localStorage;
            if (!storage) return null;

            const stored = storage.getItem(storageKey);
            return normalizeLogLevel(stored);
        } catch {
            return null;
        }
    };

    const setStoredLevel = (level) => {
        const targetWindow = getWindow();
        try {
            const storage = targetWindow?.localStorage;
            if (!storage) return;

            storage.setItem(storageKey, level);
        } catch {
            // Ignore storage access failures in private mode / denied storage.
        }
    };

    const clearStoredLevel = () => {
        const targetWindow = getWindow();
        try {
            const storage = targetWindow?.localStorage;
            if (!storage) return;

            storage.removeItem(storageKey);
        } catch {
            // Ignore storage access failures in private mode / denied storage.
        }
    };

    const getEnvironmentLevel = () => {
        const targetWindow = getWindow();
        return normalizeLogLevel(targetWindow?.environment?.LOGGING?.LEVEL);
    };

    const getEffectiveLevel = () => {
        const targetWindow = getWindow();
        return getStoredLevel() || getEnvironmentLevel() || getDefaultLogLevel(targetWindow?.environment);
    };

    const shouldLog = (level) => {
        const normalizedLevel = normalizeLogLevel(level);
        if (!normalizedLevel) return false;

        const effectiveLevel = getEffectiveLevel();
        return LEVEL_PRIORITIES[normalizedLevel] >= LEVEL_PRIORITIES[effectiveLevel];
    };

    const log = (level, ...args) => {
        const normalizedLevel = normalizeLogLevel(level);
        if (!normalizedLevel || !shouldLog(normalizedLevel)) return;

        const targetWindow = getWindow();
        const targetConsole = getConsoleForWindow(targetWindow);
        const logMethod = targetConsole?.[normalizedLevel];
        if (typeof logMethod !== 'function') return;

        logMethod(prefix, ...args.map(evaluateArgSafely).map((arg) => sanitizeArg(arg)));
    };

    const emitRuntimeConfirmation = (message) => {
        const targetWindow = getWindow();
        const targetConsole = getConsoleForWindow(targetWindow);
        const confirmationMethod = targetConsole?.info || targetConsole?.log;
        if (typeof confirmationMethod !== 'function') return;

        confirmationMethod(prefix, message);
    };

    return {
        error: (...args) => log('error', ...args),
        warn: (...args) => log('warn', ...args),
        info: (...args) => log('info', ...args),
        debug: (...args) => log('debug', ...args),
        setLevel: (level) => {
            const normalizedLevel = normalizeLogLevel(level);
            if (!normalizedLevel) return getEffectiveLevel();

            setStoredLevel(normalizedLevel);
            const effectiveLevel = getEffectiveLevel();
            emitRuntimeConfirmation(`Log level set to "${effectiveLevel}"`);
            return effectiveLevel;
        },
        getLevel: () => getEffectiveLevel(),
        clearLevel: () => {
            clearStoredLevel();
            const effectiveLevel = getEffectiveLevel();
            emitRuntimeConfirmation(`Log level override cleared; effective level is "${effectiveLevel}"`);
            return effectiveLevel;
        },
        isDebugEnabled: () => shouldLog('debug'),
    };
};

export const attachLoggerRuntimeApi = (targetWindow, loggerInstance) => {
    if (!targetWindow || !loggerInstance) return;

    targetWindow.CCLogger = {
        setLevel: loggerInstance.setLevel,
        getLevel: loggerInstance.getLevel,
        clearLevel: loggerInstance.clearLevel,
        isDebugEnabled: loggerInstance.isDebugEnabled,
    };
};

export const logger = createLogger();
attachLoggerRuntimeApi(getWindowObject(), logger);
